import { useEffect, useState, type ChangeEvent } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ApiError } from '../lib/api';
import {
  ANALYTICS_WINDOW_OPTIONS,
  DEFAULT_WINDOW_DAYS,
  getAnalyticsRequest,
  type AnalyticsSummaryResponse,
} from '../lib/analytics-api';

type PageStatus = 'loading' | 'ready' | 'error';

/** Persian labels for the backend execution status vocabulary. */
const STATUS_LABELS: Record<string, string> = {
  pending: 'در انتظار',
  pending_retry: 'در انتظار تلاش مجدد',
  success: 'موفق',
  failed: 'ناموفق',
  blocked: 'مسدود',
};

const STATUS_ORDER = ['success', 'failed', 'pending', 'pending_retry', 'blocked'] as const;

const WINDOW_LABELS: Record<number, string> = {
  7: '۷ روز اخیر',
  14: '۱۴ روز اخیر',
  30: '۳۰ روز اخیر',
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Generic Persian error mapping — raw server messages (which may contain
 * internal details) are never shown to the user.
 */
function pageErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'برای ادامه دوباره وارد حساب خود شوید.';
    }
    if (error.status >= 500) {
      return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
    }
  }
  return 'دریافت تحلیل‌ها ناموفق بود. دوباره تلاش کنید.';
}

/** Nothing has ever happened on this account — every metric is zero. */
function isEmptySummary(summary: AnalyticsSummaryResponse): boolean {
  return (
    summary.executions.total === 0 &&
    summary.automations.total === 0 &&
    summary.connected_accounts === 0 &&
    summary.conversations === 0 &&
    summary.inbound_messages === 0 &&
    summary.received_webhook_events === 0
  );
}

function barHeight(count: number, max: number): string {
  if (count === 0) {
    return '4px';
  }
  const percent = Math.round((count / max) * 100);
  return `${Math.max(6, percent)}%`;
}

export default function AnalyticsPage() {
  useDocumentTitle('تحلیل‌ها');

  const [status, setStatus] = useState<PageStatus>('loading');
  const [summary, setSummary] = useState<AnalyticsSummaryResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [days, setDays] = useState<number>(DEFAULT_WINDOW_DAYS);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getAnalyticsRequest(days)
      .then((response) => {
        if (!cancelled) {
          setSummary(response);
          setErrorMessage(null);
          setStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSummary(null);
          setErrorMessage(pageErrorMessage(error));
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [days, reloadKey]);

  function handleWindowChange(event: ChangeEvent<HTMLSelectElement>): void {
    setDays(Number(event.target.value));
    setSummary(null);
    setErrorMessage(null);
    setStatus('loading');
  }

  function handleRetry(): void {
    setErrorMessage(null);
    setStatus('loading');
    setReloadKey((key) => key + 1);
  }

  const ready = status === 'ready' && summary !== null;
  const empty = ready && isEmptySummary(summary);
  const windowTotal = ready
    ? summary.daily_executions.reduce((sum, bucket) => sum + bucket.count, 0)
    : 0;
  const maxDaily = ready
    ? Math.max(1, ...summary.daily_executions.map((bucket) => bucket.count))
    : 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="تحلیل‌ها"
        description="شاخص‌های قابل اتکا از داده‌های ثبت‌شده: اجراها، مکالمات و اتصال‌ها"
      />

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <label
            htmlFor="analytics-window"
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            بازه زمانی
          </label>
          <select
            id="analytics-window"
            data-testid="analytics-window-select"
            value={days}
            onChange={handleWindowChange}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 sm:w-48"
          >
            {ANALYTICS_WINDOW_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {WINDOW_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
        {ready && (
          <p data-testid="generated-at" className="text-xs text-slate-400">
            به‌روزرسانی: {formatTimestamp(summary.generated_at)}
          </p>
        )}
      </div>

      {status === 'loading' && (
        <div
          role="status"
          data-testid="analytics-loading"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        >
          <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
          <p className="mt-3 text-sm text-slate-500">در حال دریافت تحلیل‌ها…</p>
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-xl border border-rose-200 bg-white p-6 text-center">
          <p role="alert" data-testid="analytics-error" className="text-sm text-rose-700">
            {errorMessage}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            تلاش مجدد
          </button>
        </div>
      )}

      {empty && (
        <div
          data-testid="empty-state"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        >
          <h3 className="text-base font-bold text-slate-800">
            هنوز داده‌ای برای تحلیل ثبت نشده است
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            پس از دریافت رویدادها و اجرای اتومیشن‌ها، شاخص‌ها در اینجا نمایش داده می‌شوند.
          </p>
        </div>
      )}

      {ready && !empty && summary !== null && (
        <>
          <div data-testid="stat-cards" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div data-testid="stat-executions">
              <StatCard
                label="کل اجراها"
                value={String(summary.executions.total)}
                hint="شمارش کل رکوردهای اجرا"
              />
            </div>
            <div data-testid="stat-active-automations">
              <StatCard
                label="اتومیشن‌های فعال"
                value={String(summary.automations.enabled)}
                hint={`از مجموع ${summary.automations.total} اتوماسیون`}
              />
            </div>
            <div data-testid="stat-conversations">
              <StatCard
                label="مکالمات"
                value={String(summary.conversations)}
                hint="مکالمه‌های ثبت‌شده"
              />
            </div>
            <div data-testid="stat-inbound-messages">
              <StatCard
                label="پیام‌های ورودی"
                value={String(summary.inbound_messages)}
                hint="فقط پیام‌های دریافتی ذخیره‌شده"
              />
            </div>
            <div data-testid="stat-webhook-events">
              <StatCard
                label="رویدادهای وب‌هوک"
                value={String(summary.received_webhook_events)}
                hint="رویدادهای دریافتی حساب‌های شما"
              />
            </div>
            <div data-testid="stat-connected-accounts">
              <StatCard
                label="حساب‌های متصل"
                value={String(summary.connected_accounts)}
                hint="اتصال فعال اینستاگرام"
              />
            </div>
          </div>

          <section
            data-testid="status-breakdown"
            className="rounded-xl border border-slate-200 bg-white p-5"
          >
            <h2 className="text-sm font-bold text-slate-900">وضعیت اجراها</h2>
            <dl className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {STATUS_ORDER.map((statusKey) => (
                <div
                  key={statusKey}
                  data-testid={`status-${statusKey}`}
                  className="rounded-lg bg-slate-50 p-3"
                >
                  <dt className="text-xs text-slate-500">{statusLabel(statusKey)}</dt>
                  <dd
                    data-testid={`status-count-${statusKey}`}
                    className="mt-1 text-lg font-bold text-slate-900"
                  >
                    {summary.executions.by_status[statusKey] ?? 0}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section
            data-testid="daily-chart"
            className="rounded-xl border border-slate-200 bg-white p-5"
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-bold text-slate-900">
                اجراها در {summary.window_days} روز اخیر
              </h2>
              <p className="text-xs text-slate-500">
                مجموع در این بازه:{' '}
                <span data-testid="window-total" className="font-semibold text-slate-700">
                  {windowTotal}
                </span>
              </p>
            </div>
            <div
              role="img"
              aria-label="نمودار تعداد اجراها بر اساس روز"
              className="mt-4 flex h-40 items-end gap-1"
            >
              {summary.daily_executions.map((bucket) => (
                <div
                  key={bucket.date}
                  data-testid="daily-bar"
                  data-date={bucket.date}
                  data-count={bucket.count}
                  title={`${bucket.date}: ${bucket.count}`}
                  className="flex-1 rounded-t bg-indigo-500"
                  style={{ height: barHeight(bucket.count, maxDaily) }}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              هر ستون یک روز به وقت UTC است؛ روز جاری تاکنون شمرده می‌شود.
            </p>
          </section>
        </>
      )}

      <section
        data-testid="definitions"
        className="rounded-xl border border-slate-200 bg-white p-5"
      >
        <h2 className="text-sm font-bold text-slate-900">تعریف شاخص‌ها و محدودیت‌ها</h2>
        <ul className="mt-3 list-disc space-y-2 pr-5 text-xs leading-6 text-slate-500">
          <li>
            هر «اجرا» یک رکورد در جدول اتوماسیشن‌هاست؛ شمارش بر اساس زمان ثبت رکورد (شروع اجرا) است
            و روزها به وقت UTC گروه‌بندی می‌شوند.
          </li>
          <li>اجراهای مربوط به اتوماسیون حذف‌شده در این آمار نمی‌آیند.</li>
          <li>
            «پیام‌های ورودی» فقط شامل پیام‌های دریافتی ذخیره‌شده است؛ پیام‌های خروجی در داده‌های
            فعلی ثبت نمی‌شوند.
          </li>
          <li>«اتوماسیون فعال» یعنی اتوماسیونی که وضعیت فعال دارد.</li>
          <li>«حساب متصل» فقط اتصال‌های با وضعیت متصل را می‌شمارد.</li>
          <li>رویدادهای وب‌هوک بدون حساب شناخته‌شده به هیچ کاربری تعلق ندارند و شمرده نمی‌شوند.</li>
          <li>
            جداول آمار تجمیعی analytics_events و account_daily_stats و automation_daily_stats در این
            نسخه پر نمی‌شوند و استفاده نمی‌شوند؛ همه اعداد بالا از جداول عملیاتی خوانده می‌شوند.
          </li>
        </ul>
      </section>
    </div>
  );
}
